/**
 * useTrend — 4-week rolling activity trend for the calling user OR the team.
 *
 * Calls /api/me/trend or /api/team/trend (manager+). Returns weekly buckets
 * for outreach, meetings, quals, closes plus conversion rates.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/session";

export interface TrendWeek {
  weekStart: string; // ISO date Monday
  outreach: number;
  meetings: number;
  quals: number;
  closes: number;
  closedWonAmount: number;
}

export interface TrendConversions {
  outreachToMeeting: number; // 0..1
  meetingToQual: number;
  qualToClose: number;
}

export interface TrendPayload {
  weeks: TrendWeek[];
  conversions: TrendConversions[];
  triggers: string[];
}

const empty: TrendPayload = { weeks: [], conversions: [], triggers: [] };

export function useMyTrend() {
  const [data, setData] = useState<TrendPayload>(empty);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api<TrendPayload>("/api/me/trend");
        if (!cancelled && r) setData({ ...empty, ...r });
      } catch { /* tolerate */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);
  return { trend: data, loading };
}

export function useTeamTrend(scope: "team" | "rollup" = "team") {
  const [data, setData] = useState<any>({ reps: [], team: empty });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const path = scope === "rollup" ? "/api/team/trend/roll-up" : "/api/team/trend";
        const r = await api<any>(path);
        if (!cancelled && r) setData(r);
      } catch { /* tolerate */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [scope]);
  return { team: data, loading };
}
